import React from "react"

/**
 * Cursor-anchored zoom, plus drag-to-pan, for the Modern ECG stage.
 *
 * The figure itself is NOT redrawn when the reviewer zooms. <ModernEcgGrid/>
 * keeps rendering the page at its declared paper size and <ModernEcgStage/>
 * puts a CSS `scale()` over it, so a wheel tick costs one transform instead of
 * a full Plotly repaint of a 12-lead figure. That is also what keeps the
 * calipers correct: the guides live inside the scaled box, so they stay pinned
 * to the same samples, and <ModernCaliperLayer/> divides the pointer offsets by
 * the same factor before converting them to millimetres — a measurement must
 * read the same at 40 % as at 400 %.
 *
 * Three behaviours worth stating, because each is a deliberate trade:
 *
 * 1. A PLAIN wheel zooms. The alternative — requiring ctrl, as a browser does —
 *    means a reviewer with an ordinary mouse cannot zoom with the mouse at all,
 *    which is the whole point of the feature. Hold shift to scroll the stage
 *    with the wheel instead, and dragging the trace pans it.
 * 2. Ctrl/⌘+wheel zooms as well, so a trackpad pinch (which the browser reports
 *    as exactly that) works without a second code path.
 * 3. Drag-to-pan is mouse and pen only. A finger already scrolls `.stage`
 *    natively; panning it a second time from here would move the trace twice as
 *    far as the finger.
 */

export const ECG_ZOOM_MIN = 0.25;
export const ECG_ZOOM_MAX = 4;

/** One toolbar press, or one press of `+` / `-`. */
export const ECG_ZOOM_STEP = 1.2;

/**
 * Wheel travel -> zoom, as an exponent so that a tick multiplies the scale
 * rather than adding to it. Zooming out from 400 % then back in therefore lands
 * exactly on 400 % again, which a linear step does not.
 */
const WHEEL_SENSITIVITY = 0.0022;

/** deltaMode 1 and 2 report lines and pages; Firefox still uses lines. */
const LINE_PX = 16;
const PAGE_PX = 400;

/** Beyond this the wheel is treated as one gesture, not a flick. */
const MAX_WHEEL_PX = 120;

export function clampZoom(value: number): number {
    if (typeof value !== "number" || isNaN(value)) {
        return 1
    }

    return Math.min(ECG_ZOOM_MAX, Math.max(ECG_ZOOM_MIN, value))
}

function wheelPixels(event: WheelEvent): number {
    let delta = event.deltaY;

    if (event.deltaMode === 1) {
        delta = delta * LINE_PX
    } else if (event.deltaMode === 2) {
        delta = delta * PAGE_PX
    }

    return Math.max(-MAX_WHEEL_PX, Math.min(MAX_WHEEL_PX, delta))
}

/**
 * The point the reviewer asked to keep still, held between the zoom change and
 * the layout pass that puts it back under the cursor.
 */
type Anchor = {
    clientX: number
    clientY: number
    /** The figure point under the cursor, in UNSCALED figure pixels. */
    localX: number
    localY: number
}

type UseEcgZoomInput = {
    zoom: number
    setZoom(next: number): void
    /** The box the scale transform is applied to — see <ModernEcgStage/>. */
    boxRef: React.MutableRefObject<HTMLDivElement | null>
    /** False while the calipers own the pointer, so a drag measures instead. */
    panEnabled: boolean
}

export type EcgZoom = {
    /** Ref callback for the scrolling stage. */
    stageRef(node: HTMLDivElement | null): void
    zoomIn(): void
    zoomOut(): void
    reset(): void
}

/**
 * @param input
 */
export default function useEcgZoom(input: UseEcgZoomInput): EcgZoom {
    const {zoom, setZoom, boxRef, panEnabled} = input;

    /*
     * State-backed rather than a plain ref: the stage is not in the tree while
     * the record is still fetching, so an effect keyed on a MutableRefObject
     * would run once against `null`, bind its listeners to nothing, and never
     * get a second chance once the trace arrived.
     */
    const [stage, setStage] = React.useState<HTMLDivElement | null>(null);

    // Read inside listeners that are bound once per stage element.
    const zoomRef = React.useRef(zoom);
    const setZoomRef = React.useRef(setZoom);
    const panRef = React.useRef(panEnabled);
    const anchorRef = React.useRef<Anchor | null>(null);

    zoomRef.current = zoom;
    setZoomRef.current = setZoom;
    panRef.current = panEnabled;

    const applyZoom = React.useCallback((next: number, clientX: number, clientY: number) => {
        const from = zoomRef.current;
        const value = clampZoom(next);

        if (value === from) {
            return
        }

        const box = boxRef.current;

        if (box !== null) {
            const rect = box.getBoundingClientRect();

            anchorRef.current = {
                clientX: clientX,
                clientY: clientY,
                localX: (clientX - rect.left) / from,
                localY: (clientY - rect.top) / from,
            }
        }

        setZoomRef.current(value)
    }, [boxRef]);

    /** Middle of the visible stage — the anchor for the toolbar and the keys. */
    const stageCentre = React.useCallback(() => {
        if (stage === null) {
            return {x: 0, y: 0}
        }

        const rect = stage.getBoundingClientRect();

        return {x: rect.left + (rect.width / 2), y: rect.top + (rect.height / 2)}
    }, [stage]);

    const zoomIn = React.useCallback(() => {
        const centre = stageCentre();

        applyZoom(zoomRef.current * ECG_ZOOM_STEP, centre.x, centre.y)
    }, [applyZoom, stageCentre]);

    const zoomOut = React.useCallback(() => {
        const centre = stageCentre();

        applyZoom(zoomRef.current / ECG_ZOOM_STEP, centre.x, centre.y)
    }, [applyZoom, stageCentre]);

    const reset = React.useCallback(() => {
        const centre = stageCentre();

        applyZoom(1, centre.x, centre.y)
    }, [applyZoom, stageCentre]);

    React.useEffect(() => {
        if (stage === null) {
            return undefined
        }

        let panPointer: number | null = null;
        let panClientX = 0;
        let panClientY = 0;
        let panScrollLeft = 0;
        let panScrollTop = 0;

        function handleWheel(event: WheelEvent) {
            // The documented escape hatch: shift+wheel scrolls the stage the way
            // the wheel did before this hook existed.
            if (event.shiftKey || stage === null) {
                return
            }

            /*
             * Non-passive, so this is allowed. React's own onWheel is not an
             * option here: React 16 delegates to `document`, where Chrome forces
             * wheel listeners passive, and the page would scroll underneath the
             * zoom.
             */
            event.preventDefault();

            applyZoom(
              zoomRef.current * Math.exp(-wheelPixels(event) * WHEEL_SENSITIVITY),
              event.clientX,
              event.clientY,
            )
        }

        function endPan() {
            if (panPointer === null || stage === null) {
                return
            }

            if (stage.hasPointerCapture(panPointer)) {
                stage.releasePointerCapture(panPointer)
            }

            panPointer = null
        }

        function handlePointerDown(event: PointerEvent) {
            if (!panRef.current || stage === null || panPointer !== null) {
                return
            }

            // A finger already scrolls this box natively — panning it from here
            // as well would move the trace twice as far as the contact.
            if (event.pointerType === "touch") {
                return
            }

            if (event.button !== 0 || !event.isPrimary) {
                return
            }

            // Grabbing a scrollbar still reports a pointerdown on the element in
            // Chrome; the client box excludes the scrollbar gutters.
            const rect = stage.getBoundingClientRect();

            if (event.clientX >= rect.left + stage.clientWidth || event.clientY >= rect.top + stage.clientHeight) {
                return
            }

            panClientX = event.clientX;
            panClientY = event.clientY;
            panScrollLeft = stage.scrollLeft;
            panScrollTop = stage.scrollTop;

            try {
                stage.setPointerCapture(event.pointerId)
            } catch (error) {
                // the pointer was already gone — this pan simply never starts
                return
            }

            panPointer = event.pointerId;

            // stops the drag turning into a text selection across the figure
            event.preventDefault()
        }

        function handlePointerMove(event: PointerEvent) {
            if (panPointer === null || event.pointerId !== panPointer || stage === null) {
                return
            }

            stage.scrollLeft = panScrollLeft - (event.clientX - panClientX);
            stage.scrollTop = panScrollTop - (event.clientY - panClientY)
        }

        function handlePointerEnd(event: PointerEvent) {
            if (panPointer === null || event.pointerId !== panPointer) {
                return
            }

            endPan()
        }

        stage.addEventListener("wheel", handleWheel, {passive: false});
        stage.addEventListener("pointerdown", handlePointerDown);
        stage.addEventListener("pointermove", handlePointerMove);
        stage.addEventListener("pointerup", handlePointerEnd);
        stage.addEventListener("pointercancel", handlePointerEnd);
        stage.addEventListener("lostpointercapture", handlePointerEnd);

        return () => {
            endPan();
            stage.removeEventListener("wheel", handleWheel);
            stage.removeEventListener("pointerdown", handlePointerDown);
            stage.removeEventListener("pointermove", handlePointerMove);
            stage.removeEventListener("pointerup", handlePointerEnd);
            stage.removeEventListener("pointercancel", handlePointerEnd);
            stage.removeEventListener("lostpointercapture", handlePointerEnd)
        }
    }, [stage, applyZoom]);

    /*
     * Put the anchored point back under the cursor.
     *
     * Layout, not passive: the box has already been re-sized by this commit, so
     * reading its rect here gives the position at the NEW scale, and correcting
     * the scroll before the browser paints means the trace never visibly jumps.
     */
    React.useLayoutEffect(() => {
        const anchor = anchorRef.current;

        anchorRef.current = null;

        if (anchor === null || stage === null) {
            return
        }

        const box = boxRef.current;

        if (box === null) {
            return
        }

        const rect = box.getBoundingClientRect();

        stage.scrollLeft += (rect.left + (anchor.localX * zoom)) - anchor.clientX;
        stage.scrollTop += (rect.top + (anchor.localY * zoom)) - anchor.clientY
    }, [zoom, stage, boxRef]);

    return {stageRef: setStage, zoomIn: zoomIn, zoomOut: zoomOut, reset: reset}
}
